'use client';

// import { logger } from '@/lib/default-logger';
import { useComponentLogger } from '@/lib/component-logger';
import { useContext, useEffect, useState, useRef } from 'react';
import { fetchExprData, fetchTooltip, extractTrackedParams, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import DataContext from '@/contexts/DataContext';
import { NoSsr } from '@/components/core/no-ssr';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Area, AreaChart, Legend } from 'recharts';
import Paper from '@mui/material/Paper'
import { chartColors } from '@/styles/theme/colors';
import { getDefaultColor } from '@/lib/dataHandlers/ChartHelpers';
import { dayjs } from '@/lib/dayjs';
import { InfoTooltip } from '../typography/info-tooltip';
import CircularProgress from '@mui/material/CircularProgress';

export function DynamicChart({ config, isLoading=false, setIsLoading, chartParams={} }) {
  const logger = useComponentLogger("Dynamic Chart");

  const chartHeight = 350;

  const { currentProject, changedParams } = useContext(DataContext);
  const [dynamicData, setDynamicData] = useState({});
  const [parsedNonNumericalData, setParsedNonNumericalData] = useState({});
  const [tooltip, setTooltip] = useState(null);
  const [title, setTitle] = useState(config?.title || '');
  const [testColorMapping, setTestColorMapping] = useState({});
  // const [isLoading, setIsLoading] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const trackedParamValsRef = useRef({});
  const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr

  logger.debug('dynamic obj', config)

  const fetchContent = async () => {
    if (!config.dataSource) return;
  
    const dataSource = config.dataSource;
    
  
    if (dataSource.type === 'text') {
      setIsDataAvailable(true);
      setDynamicData({ default: config.dataSource.text });
    } else if (dataSource.type === 'apiExpr') {
      const data = await fetchExprData(currentProject, chartParams, dataSource.expr);
      logger.debug('dynamic api data', data);
  
      if (data?.rows && data.rows.length > 0 && data?.cols && data.cols.length > 0) {
        setIsDataAvailable(true);
        let parsedNumericalData = {};
        let parsedNonNumericalData = {};
        let colorMapping = {};

        // keep a count of the nav names to handle duplicates
        // format: {navName: count}
        let columnNames = {};
        for (const col of data.cols) {
          let navName = col.meta?.navName || null;
          if (navName === null) continue;
          if (columnNames.hasOwnProperty(navName)) {
            columnNames[navName] += 1;
          } else columnNames[navName] = 1;
        }

        // logger.debug('col names', columnNames, data.cols);

        // Create column mapping
        const columnMapping = data.cols.reduce((acc, col) => {
          let displayName = null;
          if (col.meta?.dis) {
            displayName = col.meta.dis;
          } else if (col.meta?.navName && columnNames[col.meta.navName] === 1) { // don't use navName if there's duplicates
            displayName = col.meta.navName;
          } else if (col.meta?.id?.dis) {
            displayName = col.meta.id.dis;
          } else if (col.name) displayName = col.name;

          if (displayName === null) return acc;

          const columnObject = {...col, mobileName: displayName};
 
          acc[col.name] = columnObject;
          return acc;
        }, {});

        // logger.debug('col map', columnMapping);
        

        // Helper function to extract value from complex objects
        const extractValue = (val) => {
          if (val === null || val === undefined) return null;
          if (typeof val === 'object') {
            // Handle boolean values in objects
            if ('val' in val) {
              const extractedVal = val.val;
              if (typeof extractedVal === 'boolean') {
                return extractedVal ? 1 : 0;
              }
              return extractedVal;
            }
          }
          // Handle direct boolean values
          if (typeof val === 'boolean') {
            return val ? 1 : 0;
          }
          return val;
        };

        // Helper function to determine if a value is non-numerical
         const isNonNumerical = (val, unit, key) => {
          if (val === null || val === undefined) return false;
          // if (key === 'v10') logger.debug('isNonNumerical: val', val, 'unit', unit, 'type');

          if (unit) { // use unit tag if it exists
            const nonNumericalUnits = ['bool', 'boolean']; // so far, bools are the only non-numerical unit?
            return nonNumericalUnits.includes(unit.toLowerCase());

          } else { // fallback to using the value to determine numerical category
            const extractedVal = typeof val === 'object' && 'val' in val ? val.val : val;
            // if (key === 'v0') logger.debug('isNonNumerical: val', val, 'extractedVal', extractedVal,'unit', unit, 'type', typeof extractedVal);
            return typeof extractedVal === 'boolean' || 
                  (typeof extractedVal === 'string' && extractedVal.toLowerCase() === 'runtime') 
            }
        };

        // First pass: Collect all unique timestamps and series names
        const allTimestamps = new Set();
        const seriesByUnit = {
          numerical: {},
          nonNumerical: {}
        };

        
        data.rows.forEach((row) => {
          const ts = extractValue(row.ts);
          if (ts) allTimestamps.add(ts);
          
          for (const key in row) {
            if (key === 'ts') continue;
            
            const colObj = columnMapping[key];
            if (!colObj) continue;
            
            const displayName = colObj?.mobileName;
            const rawValue = row[key];
            const value = extractValue(rawValue);
            const unit = (typeof rawValue === 'object' && rawValue?.unit) || colObj?.meta?.unit || '';
            
            // Determine if the value is non-numerical
            // const isNonNumericalValue = isNonNumerical(rawValue);
            const isNonNumericalValue = isNonNumerical(rawValue, unit, key);
            const targetObject = isNonNumericalValue ? seriesByUnit.nonNumerical : seriesByUnit.numerical;

            // if (key === 'v10') logger.debug('colObj', colObj, 'rawVal', rawValue, 'val', value, 'unit', unit, 'targetObj', targetObject, 'isNonNumerical', isNonNumericalValue);
            
            if (!targetObject[unit]) targetObject[unit] = new Set();
            targetObject[unit].add(displayName);
            
            // If a specific color is defined in metadata, use it
            if (!(displayName in colorMapping) && colObj.meta?.color) {
              let colorData = {
                hex: chartColors[colObj.meta.color] || colObj.meta.color,
                color: colObj.meta.color
              };
              
              colorMapping[displayName] = colorData.hex;
            }
            
          }
        });

        // logger.debug('series by unit', seriesByUnit);

        // sort series alphabetically
        for (let type in seriesByUnit) {
          for (let unit in seriesByUnit[type]) {
            seriesByUnit[type][unit] = Array.from(seriesByUnit[type][unit]).sort();
          }
        }
        
        // Second pass: Create complete data points for all timestamps
        const timestamps = Array.from(allTimestamps).sort();
        
        // Initialize data structures - group cols by chart and series type (numerical vs non numerical)
        for (const unit in seriesByUnit.numerical) {
          const seriesNames = Array.from(seriesByUnit.numerical[unit]);
          if (!parsedNumericalData[unit]) parsedNumericalData[unit] = [];
          
          timestamps.forEach(ts => {
            const dataPoint = { ts };
            seriesNames.forEach(name => {
              dataPoint[name] = null;
            });
            parsedNumericalData[unit].push(dataPoint);
          });
        }
        
        for (const unit in seriesByUnit.nonNumerical) {
          const seriesNames = Array.from(seriesByUnit.nonNumerical[unit]);
          if (!parsedNonNumericalData[unit]) parsedNonNumericalData[unit] = [];
          
          timestamps.forEach(ts => {
            const dataPoint = { ts };
            seriesNames.forEach(name => {
              dataPoint[name] = 0; // Initialize with 0 instead of null for boolean values
            });
            parsedNonNumericalData[unit].push(dataPoint);
          });
        }

        /**
         * Assign default colors to columns without a color tag
         * Each chart assigns the same cycle of colors (e.g. chart A is blue and red, chart B is blue and red etc)
         * Charts are grouped into sets contained in seriesByUnit.seriesType
         */
        for (const seriesType in seriesByUnit) {
          for (const unit in seriesByUnit[seriesType]) {
            const seriesNames = Array.from(seriesByUnit[seriesType][unit]);
            let colorIndex = 0;
            for (const name of seriesNames) {
              if (!(name in colorMapping)) {
                let colorData = getDefaultColor(colorIndex);
                colorIndex++;
                colorMapping[name] = colorData.hex;
              }
            }
          }
        }
        
        // Third pass: Fill in actual values
        data.rows.forEach((row) => {
          const ts = extractValue(row.ts);
          if (!ts) return;
          
          for (const key in row) {
            if (key === 'ts') continue;
            
            const colObj = columnMapping[key];
            if (!colObj) continue;
            
            const displayName = colObj?.mobileName;
            const rawValue = row[key];
            let value = extractValue(rawValue);
            const unit = (typeof rawValue === 'object' && rawValue?.unit) || colObj?.meta?.unit || '';
            
            // Handle non-numerical values
            if (isNonNumerical(rawValue, unit, key)) {
              if (typeof value === 'string') {
                value = value.toLowerCase() === 'runtime' ? 1 : 0;
              }
              const dataPoint = parsedNonNumericalData[unit]?.find(dp => dp.ts === ts);
              if (dataPoint) {
                dataPoint[displayName] = value;
              }
            } else if (typeof value === 'number') {
              const dataPoint = parsedNumericalData[unit]?.find(dp => dp.ts === ts);
              if (dataPoint) {
                dataPoint[displayName] = Math.round(value * 100) / 100; // round to 2 decimal places
              }
            }
          }
        });
                                                                                                                                                                                                                                                                                                                                                                                                                                
        logger.debug('dynamic parsed', parsedNumericalData, parsedNonNumericalData);
        setDynamicData(parsedNumericalData);
        setParsedNonNumericalData(parsedNonNumericalData);
        setTestColorMapping(colorMapping);
      } else {
        // setIsLoading(false);
        setIsDataAvailable(false);
      }

    }
  };


  const processTooltip = async () => {
    let tooltipContent = await fetchTooltip(currentProject, chartParams, config);
    if (tooltipContent) setTooltip(tooltipContent);
  };

  useEffect(() => {
    setDynamicData({});
    setParsedNonNumericalData({});
    // fetchContent().catch((error) => console.error('Error in fetchContent:', error));
    let trackedParams = extractTrackedParams(config.dataSource);
    let params = {};
    for (const param of trackedParams) {
        if (params[param]) params[param] = params[param].value;
    }

    logger.debug('tracked params', params, chartParams, trackedParams);
    trackedParamValsRef.current = params;
    trackedParamNamesRef.current = trackedParams;
    try {
      // setIsLoading(true);
      fetchContent();
    } catch (err) {
      logger.error('Error in fetchContent:', err);
    } finally {
      logger.debug('finished render');
      // if (Object.keys(dynamicData).length > 0) setIsLoading(false);
    }

    processTooltip().catch(() => {});
    logger.debug('dynamic init', config, chartParams);
  }, []);

  useEffect(() => {
    logger.debug("changed params start", changedParams, chartParams);
      let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, chartParams, trackedParamValsRef.current);
      logger.debug('changed params extract', changedParams, trackedParamNamesRef.current, shouldReFetch);
      if (shouldReFetch.changed) {
          trackedParamValsRef.current = shouldReFetch.paramRef;
          try {
            // setIsLoading(true);
            fetchContent();
          } catch (err) {
            logger.error('Error in fetchContent:', err);
          } finally {
            logger.debug('finished render');
            // if (Object.keys(dynamicData).length > 0) setIsLoading(false);
          }
          processTooltip().catch(() => {});
      }

      // make sure to clear the content if any of the tracked params were removed
      // common use case: when a parent param changes, it clears the child param
      for (const param of trackedParamNamesRef.current) { 
          if (!chartParams[param]) {
              setDynamicData({});
              return;
          }
      }
    }, [changedParams]);

  useEffect(() => {
    logger.debug('dynamic data change', dynamicData);
    // if (Object.keys(dynamicData).length > 0) setIsLoading(false);
  }, [dynamicData]);

  useEffect(() => {
    logger.debug('chart param change', chartParams);
    // if (Object.keys(dynamicData).length > 0) setIsLoading(false);
  }, [chartParams]);

  const formatXAxisTick = (tick) => {
    return dayjs(tick).format('MM/DD HH:mm');
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '5rem' }}>
              <CircularProgress />
            </Box>
  }

  if (!isDataAvailable) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '5rem' }}>
              <Typography variant='subtitle1' color={'text.secondary'}>No data available</Typography>
            </Box>
  }

  return (
    <Stack spacing={0} sx={{ 
      margin: 0, 
      padding: 0,
      '& .recharts-responsive-container': {
        width: '100% !important'
      } 
    }}>
      {title ? 
      <Box sx={{ flex: '1 1 auto'}}>
        <Typography color="text.secondary" variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          {title}
          {tooltip ? <InfoTooltip content={tooltip} /> : null}
        </Typography>
      </Box>
        : null
      }
      <NoSsr fallback={<Box sx={{ height: `${chartHeight}px` }} />}>
        {/* Rendering Numerical Charts */}
        {Object.keys(dynamicData)?.map((unit) => {
          return (
            <Box key={unit} sx={{ width: '100%' }}>
              <ResponsiveContainer height={chartHeight} width="100%">
                <LineChart data={dynamicData[unit]} margin={{ top: 0, right: 30, bottom: 50, left: 3 }}>
                  <CartesianGrid strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="ts" angle={-45} textAnchor="end" tickFormatter={formatXAxisTick} />
                  <YAxis 
                  // tick={{ angle: -45 }}
                    axisLine={false} 
                    domain={['auto', 'auto']} 
                    width={80} 
                    label={{ value: unit, angle: -90, position: 'insideLeft' }} 
                    // tickFormatter={(value) => {
                    //   if (value >= 1000) {
                    //     // Convert to scientific notation
                    //     const exp = Math.floor(Math.log10(value));
                    //     const base = value / Math.pow(10, exp);
                    //     return `${base.toFixed(1)}×10^${exp}`;
                    //   }
                    //   return value;
                    // }}
                  />
                  {Object.keys(dynamicData[unit][0] || {}).filter((key) => key !== 'ts').map((key) => (
                    <Line key={key} dataKey={key} strokeWidth={2} stroke={testColorMapping[key]} dot={false} connectNulls={true} type="monotone" />
                  ))}
                  <Legend verticalAlign='top' align='center' wrapperStyle={{ paddingBottom: 20, fontSize: 14 }} />
                  <Tooltip animationDuration={50} content={<TooltipContent />} cursor={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          );
        })}
        {/* Rendering Boolean Charts */}
        {Object.keys(parsedNonNumericalData)?.flatMap((unit) => {
          return Object.keys(parsedNonNumericalData[unit][0] || {}).filter((key) => key !== 'ts').map((boolKey) => (
            <ResponsiveContainer height={150} width="100%" key={`${unit}-${boolKey}`}>
              <AreaChart data={parsedNonNumericalData[unit]} margin={{ top: 0, right: 0, bottom: 50, left: -10 }}>
                <CartesianGrid strokeDasharray="2 4" vertical={false} />
                <YAxis axisLine={false} domain={[0, 1]} tickCount={2} tickFormatter={(tick) => (tick === 0 || tick === 1 ? '' : tick)} />
                <Area dataKey={boolKey} name={boolKey} stroke={testColorMapping[boolKey]} fill={testColorMapping[boolKey]} strokeWidth={2} type="stepAfter" />
                <Legend verticalAlign='top' />
                <Tooltip animationDuration={50} content={<TooltipContent />} cursor={false} />
              </AreaChart>
            </ResponsiveContainer>
          ));
        })}
      </NoSsr>
    </Stack>
  );
}

function TooltipContent({ active, payload }) {
  if (!active) {
    return null;
  }
  return (
    <Paper sx={{ border: '1px solid var(--mui-palette-divider)', boxShadow: 'var(--mui-shadows-16)', p: 1 }}>
      <Stack spacing={2}>
        {payload?.map((entry) => (
          <Stack direction="row" key={entry.name} spacing={3} sx={{ alignItems: 'center' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: '1 1 auto' }}>
              <Box sx={{ bgcolor: entry.stroke, borderRadius: '2px', height: '8px', width: '8px' }} />
              <Typography sx={{ whiteSpace: 'nowrap' }}>{entry.name}</Typography>
            </Stack>
            <Typography color="text.secondary" variant="body2">
              {entry.value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

export default DynamicChart;